import type {
  CoupledDimensionGroupResult,
  DataSourceCandidatesResponse,
  DimensionSource,
  Source,
  WidgetEntity,
} from '@highimpact-frontend/api-types';
import {
  isLegacyDataSourceResponse,
  isWidgetDimensionsResponse,
  isWidgetEntitiesResponse,
} from '@highimpact-frontend/api-types';
import { Alert, Box, CircularProgress } from '@mui/material';
import React from 'react';
import { V1LegacySelector } from './v1LegacySelector';
import { V2DimensionsSelector } from './v2DimensionsSelector';
import { V2EntitiesSelector } from './v2EntitiesSelector';

// -- type definitions --

export interface WidgetSourceSelectorProps {
  data: DataSourceCandidatesResponse | null;
  loading?: boolean;
  error?: boolean;
  onAxisSelect?: (axisKey: string, selected: Source[]) => void;
  initialLegacySelection?: Record<string, Source[]>;
  onIndependentDimensionSelect?: (
    dimensionKey: string,
    selected: DimensionSource[]
  ) => void;
  onCoupledGroupSelect?: (
    groupKey: string,
    selectedRows: CoupledDimensionGroupResult[]
  ) => void;
  initialIndependentSelection?: Record<string, DimensionSource[]>;
  initialCoupledSelection?: Record<string, CoupledDimensionGroupResult[]>;
  onEntitySelect?: (selected: WidgetEntity[]) => void;
  initialEntitySelection?: WidgetEntity[];
  matchControlPanelStyle?: boolean;
  coupledGroupsSingleSelect?: boolean;
  entitiesMultiple?: boolean;
  /** Per-dimension multi-select toggles for v2 independent slots. */
  independentDimensionMultiple?: Record<string, boolean>;
  /** Optional display labels keyed by independent dimension slot name. */
  independentDimensionLabels?: Record<string, string>;
  /** Optional display labels keyed by coupled group name. */
  coupledGroupLabels?: Record<string, string>;
}

// -- main component --

/**
 * Routes data source candidate responses to the selector that matches their schema.
 */
export const WidgetSourceSelector: React.FC<WidgetSourceSelectorProps> = ({
  data,
  loading = false,
  error = false,
  onAxisSelect,
  initialLegacySelection,
  onIndependentDimensionSelect,
  onCoupledGroupSelect,
  initialIndependentSelection,
  initialCoupledSelection,
  onEntitySelect,
  initialEntitySelection,
  matchControlPanelStyle = false,
  coupledGroupsSingleSelect = true,
  entitiesMultiple = true,
  independentDimensionMultiple,
  independentDimensionLabels,
  coupledGroupLabels,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Impossible de charger les sources de données.
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  if (isLegacyDataSourceResponse(data)) {
    return (
      <V1LegacySelector
        data={data}
        onAxisSelect={onAxisSelect}
        initialSelection={initialLegacySelection}
        matchControlPanelStyle={matchControlPanelStyle}
      />
    );
  }

  if (isWidgetDimensionsResponse(data)) {
    return (
      <V2DimensionsSelector
        data={data}
        onIndependentDimensionSelect={onIndependentDimensionSelect}
        onCoupledGroupSelect={onCoupledGroupSelect}
        initialIndependentSelection={initialIndependentSelection}
        initialCoupledSelection={initialCoupledSelection}
        matchControlPanelStyle={matchControlPanelStyle}
        coupledGroupsSingleSelect={coupledGroupsSingleSelect}
        independentDimensionMultiple={independentDimensionMultiple}
        independentDimensionLabels={independentDimensionLabels}
        coupledGroupLabels={coupledGroupLabels}
      />
    );
  }

  if (isWidgetEntitiesResponse(data)) {
    return (
      <V2EntitiesSelector
        data={data}
        onEntitySelect={onEntitySelect}
        initialSelection={initialEntitySelection}
        matchControlPanelStyle={matchControlPanelStyle}
        multiple={entitiesMultiple}
      />
    );
  }

  return (
    <Alert severity="error">
      Format de réponse non pris en charge pour les sources de données.
    </Alert>
  );
};
